import type {
  MembershipTierComparisonFeatureList,
  MembershipTierWithTrial,
} from '@/lib/constants/maverick';
import { MembershipTierInfo } from '@/types/maverickTypes';
import { Currency, Language, Region } from '@whoop/i18n';
import { getFirstVariant } from 'ui';
import { getFormatters } from 'ui/hooks/formatter';
import { getPriceValues } from 'ui/utils/variantHelpers';

// Returns the feature lists to display for the given tiers
export const getFeatureListsToShow = (
  tiers: MembershipTierWithTrial[],
  membershipTierFeatureLists: MembershipTierComparisonFeatureList[],
): MembershipTierComparisonFeatureList[] => {
  return membershipTierFeatureLists
    .filter((featureList) => tiers.includes(featureList.tier))
    .sort(
      (featureList1, featureList2) =>
        tiers.indexOf(featureList1.tier) - tiers.indexOf(featureList2.tier),
    );
};

// Returns the number of placeholders needed to fill the feature list for the given tier,
// relative to the other tiers displayed
export const getNumberOfPlaceholdersToShow = (
  featureListsToShow: MembershipTierComparisonFeatureList[],
  currentFeatureListLength: number,
): number => {
  const longestListLength = Math.max(
    currentFeatureListLength,
    ...featureListsToShow.map(
      (featureList) => featureList.featuresCollection.items.length,
    ),
  );

  return Math.max(longestListLength - currentFeatureListLength, 0);
};

// Returns the formatted prices for the given tiers in the given currency
export const getFormattedTierPrices = (
  availableTiers: MembershipTierInfo[],
  currency: Currency,
  language: Language,
  region: Region,
): Record<string, string> => {
  const { formatDisplayPrice } = getFormatters({ currency, language, region });

  return Object.fromEntries(
    availableTiers.map((tier) => {
      const { originalPrice } = getPriceValues(
        getFirstVariant(tier.membershipProduct),
      );
      return [tier.name, formatDisplayPrice(originalPrice)];
    }),
  );
};
